import { useSite, useSiteUpdate } from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import {
  RecursivePartialSiteAllowStringValues,
  Site,
  SiteCalculations,
} from "@inrange/building-manager-api-client/models-site";
import {
  formatPercentage,
  formatUnits,
} from "@inrange/theme-components/formatting";
import { sortBy } from "@inrange/utils";
import deepmerge from "deepmerge";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Table } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Select from "react-select";
import { csvHeaders, formatCsvRow } from "../utils/SitesCsvUtil";
import Loading from "./Loading";
import { IN_PROGRESS_STATUSES } from "./site/operationalStatusUtils";
import { overwriteMerge } from "./site/site-utils";
import {
  isLinkedToNonTestOrg,
  isLinkedToNonTestOrgWithNameCheck,
  isProdSiteBasedOnName,
  parseAsNumber,
} from "./site/siteSections/utils";
import { getOwnership, siteIRR } from "./sites-table-utils";
import SitesTableBulkEditMenu from "./SitesTableBulkEditMenu";
import MemoizedSitesTableRow from "./SitesTableRow";

const sortOrderLabels = {
  alphabetical: ["Alphabetical", "Asc"],
  lastModified: ["Last modified", "Desc"],
  created: ["Created", "Desc"],
  cod: ["Comm. op. date", "Desc"],
  score: ["Score", "Desc"],
  irr: ["IRR", "Desc"],
  installedCapacity: ["Installed capacity", "Desc"],
  exportCapacity: ["Export capacity", "Desc"],
  curtailment: ["Curtailment", "Asc"],
  tenantTariff: ["Tenant tariff", "Desc"],
  tenantShare: ["Tenant share", "Desc"],
};

const SitesTable = ({
  isFullSiteList,
  organisation,
  sites,
  thisSiteId,
  setFilteredParks,
  orgFilterLabel,
  filterOrgs,
  setFilteredLinkedOrgIds,
  tableAlertStyle,
  setTableAlertStyle,
  tableAlertMessage,
  setTableAlertMessage,
  defaultSortOrder,
  defaultSortDirection,
  filters,
  setFilters,
  onEnterBulkReview,
}: {
  // Used to disable some functionality which doesn't work with the full site list
  isFullSiteList?: boolean;
  organisation?: Organisation;
  sites: Site[];
  thisSiteId?: string;
  setFilteredParks?: (parks: string[]) => void;
  orgFilterLabel: string;
  filterOrgs: { name: string; orgId: string }[];
  setFilteredLinkedOrgIds: (orgIds: string[]) => void;
  tableAlertStyle: string;
  setTableAlertStyle: (style: string) => void;
  tableAlertMessage: ReactNode | string | undefined;
  setTableAlertMessage: (
    message: ReactNode | string | undefined | undefined
  ) => void;
  defaultSortOrder?: string;
  defaultSortDirection?: string;
  filters?: Record<string, boolean>;
  setFilters?: (filters: Record<string, boolean>) => void;
  onEnterBulkReview?: (args: { mode: string; sites: Site[] }) => void;
}) => {
  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<string>>(
    new Set()
  );
  const [filterText, setFilterText] = useState<string>("");
  // Debounced version of filter text to avoid filtering on every keypress
  const [debouncedFilterText, setDebouncedFilterText] = useState<string>("");
  const [bulkEditMode, setBulkEditMode] = useState<undefined | string>(
    undefined
  );

  const [inlineEditMode, setInlineEditMode] = useState<boolean>(false);
  const [inlineEdits, setInlineEdits] = useState<{
    [key: string]: { site: RecursivePartialSiteAllowStringValues };
  }>({});
  const [inlineSiteCalculations, setInlineSiteCalculations] = useState<{
    [key: string]: SiteCalculations;
  }>({});

  const [sortOrder, setSortOrder] = useState<string>(
    defaultSortOrder ?? "alphabetical"
  );
  const [sortDirection, setSortDirection] = useState<string>(
    defaultSortDirection ?? "Asc"
  );

  const [page, setPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number | "all">(
    isFullSiteList ? 100 : "all"
  );

  const siteUpdate = useSiteUpdate();
  const queryClient = useQueryClient();
  const onSitesUpdate = async (
    updates: { [key: string]: any },
    options?: { actionLabel?: string }
  ) => {
    const selectedSitesForAction = Object.keys(updates);
    const actionLabel = options?.actionLabel;
    const updatingNoun = actionLabel
      ? `${actionLabel} for ${selectedSitesForAction.length} site${selectedSitesForAction.length === 1 ? "" : "s"}`
      : `${selectedSitesForAction.length} sites`;
    setBulkEditMode(undefined);
    setTableAlertStyle("secondary");
    setTableAlertMessage(`Updating ${updatingNoun}...`);

    const mutations = selectedSitesForAction.map((siteId) =>
      siteUpdate.mutateAsync({
        siteId,
        payload: {
          ...updates[siteId],
          site: { ...updates[siteId].site, id: siteId },
        },
      })
    );

    try {
      const results = await Promise.allSettled(mutations);
      if (organisation) {
        queryClient.invalidateQueries(["org", organisation.id]);
      }
      queryClient.invalidateQueries(["sites"]);
      for (const siteId of selectedSitesForAction) {
        queryClient.invalidateQueries(["site", siteId]);
      }
      const successfulUpdates = results.filter(
        (result) => result.status === "fulfilled"
      ).length;
      const failedUpdates = results.filter(
        (result) => result.status === "rejected"
      ).length;

      const failedSiteDetails = results
        .filter((result) => result.status === "rejected")
        .map((result) => {
          const siteId = selectedSitesForAction[results.indexOf(result)];
          if (result.reason?.response?.data.invalid_states) {
            const invalid_states: {
              loc: string[];
              msg: string;
              type: string;
            }[] = result.reason.response.data.invalid_states;
            const messages = invalid_states.map(
              (state) => `${state.loc.join(".")}: ${state.msg}`
            );
            const errorMessage = messages.join(", ");
            return { siteId, errorMessage };
          }
          const errorMessage =
            result.reason?.response?.data?.message ||
            result.reason?.message ||
            result.reason?.toString() ||
            "Unknown error";
          return { siteId, errorMessage };
        });

      const successCountLabel = `${successfulUpdates} site${successfulUpdates === 1 ? "" : "s"}`;
      const failureCountLabel = `${failedUpdates} out of ${selectedSitesForAction.length} site${selectedSitesForAction.length === 1 ? "" : "s"}`;
      if (failedUpdates === 0) {
        setTableAlertStyle("success");
        setTableAlertMessage(
          actionLabel
            ? `Successfully updated ${actionLabel} for ${successCountLabel}`
            : `Successfully updated ${successCountLabel}`
        );
      } else {
        setTableAlertStyle("danger");
        setTableAlertMessage(
          <>
            <div>
              {actionLabel
                ? `Failed to update ${actionLabel} for ${failureCountLabel}`
                : `Failed to update ${failureCountLabel}`}
            </div>
            <ul style={{ margin: 0 }}>
              {failedSiteDetails
                .slice(0, 10)
                .map(({ siteId, errorMessage }) => (
                  <li key={siteId}>
                    <Link to={`/site/${siteId}/edit`}>
                      {sites.find((site) => site.id === siteId)?.name ||
                        `Site ${siteId}`}
                    </Link>{" "}
                    - {errorMessage}
                  </li>
                ))}
              {failedSiteDetails.length > 10 && (
                <li>{`and ${failedSiteDetails.length - 10} more...`}</li>
              )}
            </ul>
          </>
        );
      }
    } catch (error) {
      setTableAlertStyle("danger");
      setTableAlertMessage(
        actionLabel
          ? `An unexpected error occurred while updating ${actionLabel}`
          : `An unexpected error occurred while updating sites`
      );
    }
  };

  const onSetSelected = useCallback((siteId: string, selected: boolean) => {
    setSelectedSiteIds((previous) => {
      const newSet = new Set(previous);
      if (selected) {
        newSet.add(siteId);
      } else {
        newSet.delete(siteId);
      }
      return newSet;
    });
  }, []);

  const onSetInlineEdits = useCallback(
    (
      siteId: string,
      edits: {
        site: RecursivePartialSiteAllowStringValues;
      }
    ) => {
      setInlineEdits((prevUpdates) => {
        return {
          ...prevUpdates,
          [siteId]: deepmerge(prevUpdates[siteId] ?? {}, edits, {
            arrayMerge: overwriteMerge,
          }),
        };
      });
    },
    []
  );

  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;

  const params = new URLSearchParams(window.location.search);
  const testCreateTemporaryOrg =
    params.get("testCreateTemporaryOrg") === "true";

  const csvData = useMemo(
    () =>
      isFullSiteList || !fetchSiteValues.data
        ? []
        : [...sites].sort(sortBy((s) => s.id)).map((site) => {
            return formatCsvRow(
              site,
              organisation?.id ?? "no-org",
              isLinkedToNonTestOrg(site) ? "production" : "test",
              fetchSiteValues.data!.options.pvInverters
            );
          }),
    [fetchSiteValues.data, isFullSiteList, organisation?.id, sites]
  );

  // Debounce filter text updates similar to site recalculation debounce pattern
  useEffect(() => {
    const debounceWaitMillis = testMode ? 0 : 300;
    const timeoutId = setTimeout(() => {
      setDebouncedFilterText(filterText);
    }, debounceWaitMillis);
    return () => clearTimeout(timeoutId);
  }, [filterText, testMode]);

  const [selectedSites, sortedSites, unlockedSites, totalItems]: [
    Site[],
    Site[],
    Site[],
    number,
  ] = useMemo(() => {
    const filterWords = debouncedFilterText.toLowerCase().split(" ");
    const filteredSites = sites.filter((site) => {
      // Filter by search text
      if (debouncedFilterText) {
        const siteNameLower = `${site.name.toLowerCase()}${site.id}`;
        if (!filterWords.every((word) => siteNameLower.includes(word))) {
          return false;
        }
      }
      if (!filters) {
        return true;
      }
      // Status filters are OR'd together
      const hasStatusFilter =
        filters["operational"] ||
        filters["operationalOfftaker"] ||
        filters["inprogress"] ||
        filters["started"];
      if (hasStatusFilter) {
        const isPv = site.totalInstalledCapacity > 0;
        const matchesOperationalPv =
          filters["operational"] &&
          site.operationalStatus === "operational" &&
          isPv;
        const matchesOfftaker =
          filters["operationalOfftaker"] &&
          site.operationalStatus === "operational" &&
          !isPv;
        const matchesInProgress =
          filters["inprogress"] &&
          IN_PROGRESS_STATUSES.has(site.operationalStatus);
        const matchesStarted =
          filters["started"] && site.operationalStatus !== "notStarted";
        if (
          !matchesOperationalPv &&
          !matchesOfftaker &&
          !matchesInProgress &&
          !matchesStarted
        ) {
          return false;
        }
      }
      // Filter by production/test status
      if (
        filters["production"] &&
        (!isLinkedToNonTestOrgWithNameCheck(site) ||
          !isProdSiteBasedOnName(site))
      ) {
        return false;
      }

      // Filter by locked status
      if (filters["locked"] && !site.locked) {
        return false;
      }
      return true;
    });

    const newSelectedSites = filteredSites.filter(
      (site) => selectedSiteIds.size === 0 || selectedSiteIds.has(site.id)
    );

    const sortedFilteredSites = [...filteredSites].sort((a, b) => {
      // Sorts are ascending by default
      const originalA = a;
      const originalB = b;
      if (sortDirection === "Desc") {
        [a, b] = [b, a];
      }
      let primarySort = 0;
      if (sortOrder === "alphabetical") {
        primarySort = a.name
          .trim()
          .replaceAll(" ", "")
          .toLowerCase()
          .localeCompare(b.name.trim().replaceAll(" ", "").toLowerCase());
      } else if (sortOrder === "lastModified") {
        primarySort = a.updatedAt - b.updatedAt;
      } else if (sortOrder === "created") {
        primarySort = a.createdAt - b.createdAt;
      } else if (sortOrder === "cod") {
        const aCod = a.commerciallyOperationalDate || "";
        const bCod = b.commerciallyOperationalDate || "";
        primarySort = aCod.localeCompare(bCod);
      } else if (sortOrder === "score") {
        primarySort = (a.score || 0) - (b.score || 0);
      } else if (sortOrder === "irr") {
        const aIrr = siteIRR(a, undefined, organisation);
        const bIrr = siteIRR(b, undefined, organisation);
        primarySort = (parseAsNumber(aIrr) || 0) - (parseAsNumber(bIrr) || 0);
      } else if (sortOrder === "installedCapacity") {
        primarySort = a.totalInstalledCapacity - b.totalInstalledCapacity;
      } else if (sortOrder === "exportCapacity") {
        primarySort =
          (a.totalExportLimit ?? Infinity) - (b.totalExportLimit ?? Infinity);
      } else if (sortOrder === "curtailment") {
        primarySort =
          (a.energyFlowAnnual.curtailed || 0) -
          (b.energyFlowAnnual.curtailed || 0);
      } else if (sortOrder === "tenantTariff") {
        const aTariff = a.tariffConfig.onsitePPA.tariff;
        const bTariff = b.tariffConfig.onsitePPA.tariff;
        primarySort = parseAsNumber(aTariff) - parseAsNumber(bTariff);
      } else if (sortOrder === "tenantShare") {
        primarySort =
          a.energyFlowAnnual.tenantEnergyShare -
          b.energyFlowAnnual.tenantEnergyShare;
      } else {
        throw new Error(`Unknown sort order: ${sortOrder}`);
      }

      // Apply secondary alphabetical sort if primary sort results in a tie
      return primarySort !== 0
        ? primarySort
        : originalA.name
            .trim()
            .replaceAll(" ", "")
            .toLowerCase()
            .localeCompare(
              originalB.name.trim().replaceAll(" ", "").toLowerCase()
            );
    });

    const newSortedSites = sortedFilteredSites.slice(
      (page - 1) * (itemsPerPage === "all" ? sites.length : itemsPerPage),
      page * (itemsPerPage === "all" ? sites.length : itemsPerPage)
    );

    return [
      newSelectedSites,
      newSortedSites,
      newSortedSites.filter((s) => !s.locked),
      filteredSites.length,
    ];
  }, [
    debouncedFilterText,
    sites,
    page,
    itemsPerPage,
    filters,
    selectedSiteIds,
    sortDirection,
    sortOrder,
    organisation,
  ]);

  if (fetchSiteValues.isError) {
    return <div>Error loading site values.</div>;
  }

  if (fetchSiteValues.isLoading || !fetchSiteValues.data) {
    return <Loading label="Loading site values data..." />;
  }

  const dateString = new Date().toISOString().split(".")[0];
  const csvFileName = (
    (organisation?.name ?? "") +
    " Site List " +
    dateString +
    ".csv"
  ).trim();

  return (
    <div style={{ margin: "0" }}>
      {tableAlertMessage && (
        <Alert
          variant={tableAlertStyle}
          onClose={() => setTableAlertMessage(undefined)}
          dismissible={tableAlertStyle !== "secondary"}
        >
          <div>{tableAlertMessage}</div>
        </Alert>
      )}
      <Row style={{ marginTop: "12px", marginBottom: "12px" }}>
        <Col
          xs="auto"
          style={{
            justifyContent: "flex-start",
            alignContent: "start",
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <h4>
            Sites{" "}
            <span>
              (
              {itemsPerPage !== "all" &&
                `${(page - 1) * itemsPerPage + 1} - ${(page - 1) * itemsPerPage + sortedSites.length}/`}
              {selectedSites.length})
            </span>
          </h4>
          {!isFullSiteList && (
            <div>
              <CSVLink
                data={csvData}
                headers={csvHeaders}
                filename={csvFileName}
              >
                <Button variant="primary" data-testid="download-site-data">
                  Download sites data
                </Button>
              </CSVLink>
            </div>
          )}
        </Col>
        <Col
          style={{
            justifyContent: "flex-end",
            alignContent: "end",
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <Form.Control
            type="search"
            placeholder="Search"
            value={filterText ?? ""}
            onChange={(e) => {
              setFilterText(String(e.target.value));
              setSelectedSiteIds(new Set());
            }}
            style={{ width: "auto" }}
          />
          <Select
            placeholder={`Filter by ${orgFilterLabel}...`}
            onChange={(values) => {
              setFilteredLinkedOrgIds(values.map((value) => value["value"]));
              setSelectedSiteIds(new Set());
            }}
            options={filterOrgs.map((org) => ({
              label: org.name,
              value: org.orgId,
            }))}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                border: "var(--bs-border-width) solid var(--bs-border-color)",
                borderRadius: "var(--bs-border-radius)",
                color: "var(--bs-body-color)",
                minWidth: "250px",
              }),
            }}
            isMulti
          />
          {organisation &&
            setFilteredParks &&
            organisation.parks.length > 0 && (
              <Select
                placeholder="Filter by park..."
                onChange={(values) => {
                  setFilteredParks(values.map((value) => value["value"]));
                  setSelectedSiteIds(new Set());
                }}
                options={organisation.parks.sort().map((park) => ({
                  label: park,
                  value: park,
                }))}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border:
                      "var(--bs-border-width) solid var(--bs-border-color)",
                    borderRadius: "var(--bs-border-radius)",
                    color: "var(--bs-body-color)",
                  }),
                }}
                isMulti
              />
            )}
          <SitesTableBulkEditMenu
            organisation={organisation}
            sites={sites}
            selectedSiteIds={selectedSiteIds}
            bulkEditMode={bulkEditMode}
            setBulkEditMode={setBulkEditMode}
            onSitesUpdate={onSitesUpdate}
            setTableAlertStyle={setTableAlertStyle}
            setTableAlertMessage={setTableAlertMessage}
            onEnterBulkReview={onEnterBulkReview}
          />
          {inlineEditMode && (
            <Button
              variant="primary"
              onClick={() => {
                onSitesUpdate(inlineEdits);
                setInlineEditMode(false);
                setInlineEdits({});
              }}
            >
              Save inline edits to {Object.keys(inlineEdits).length} sites
            </Button>
          )}
          {!inlineEditMode && organisation && (
            <div>
              <a
                href={`/site/new?org=${organisation?.id}${
                  testCreateTemporaryOrg
                    ? `&testCreateTemporaryOrg=true&testOrgId=${organisation?.id}`
                    : ""
                }`}
              >
                <Button variant="primary">New site</Button>
              </a>
            </div>
          )}
        </Col>
      </Row>
      <Row style={{ marginTop: "12px", marginBottom: "12px" }}>
        {organisation && (
          <Col
            xs={"auto"}
            style={{
              alignItems: "center",
              display: "flex",
            }}
          >
            <SelectedSitesStatistics
              organisation={organisation}
              selectedSites={selectedSites}
              selectedSiteIds={selectedSiteIds}
              inlineEdits={inlineEdits}
              inlineSiteCalculations={inlineSiteCalculations}
            />
          </Col>
        )}
        <Col
          style={{
            justifyContent: "flex-end",
            alignContent: "end",
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          {isFullSiteList && filters && setFilters && (
            <div className="sites-filter-toggles">
              <div className="sites-filter-label">Filter:</div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setFilters({
                    ...filters,
                    production: !filters["production"],
                  })
                }
              >
                <Form.Check
                  type="switch"
                  label="Production"
                  checked={filters["production"] || false}
                  onChange={() =>
                    setFilters({
                      ...filters,
                      production: !filters["production"],
                    })
                  }
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setFilters({
                    ...filters,
                    operational: !filters["operational"],
                  })
                }
              >
                <Form.Check
                  type="switch"
                  label="Operational PV"
                  checked={filters["operational"] || false}
                  onChange={() =>
                    setFilters({
                      ...filters,
                      operational: !filters["operational"],
                    })
                  }
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setFilters({
                    ...filters,
                    operationalOfftaker: !filters["operationalOfftaker"],
                  })
                }
              >
                <Form.Check
                  type="switch"
                  label="Operational offtaker"
                  checked={filters["operationalOfftaker"] || false}
                  onChange={() =>
                    setFilters({
                      ...filters,
                      operationalOfftaker: !filters["operationalOfftaker"],
                    })
                  }
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setFilters({
                    ...filters,
                    inprogress: !filters["inprogress"],
                  })
                }
              >
                <Form.Check
                  type="switch"
                  label="In progress"
                  checked={filters["inprogress"] || false}
                  onChange={() =>
                    setFilters({
                      ...filters,
                      inprogress: !filters["inprogress"],
                    })
                  }
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setFilters({
                    ...filters,
                    started: !filters["started"],
                  })
                }
              >
                <Form.Check
                  type="switch"
                  label="Started"
                  checked={filters["started"] || false}
                  onChange={() =>
                    setFilters({
                      ...filters,
                      started: !filters["started"],
                    })
                  }
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setFilters({
                    ...filters,
                    locked: !filters["locked"],
                  })
                }
              >
                <Form.Check
                  type="switch"
                  label="Locked"
                  checked={filters["locked"] || false}
                  onChange={() =>
                    setFilters({
                      ...filters,
                      locked: !filters["locked"],
                    })
                  }
                  style={{ cursor: "pointer" }}
                />
              </div>
            </div>
          )}
          <Form.Group controlId="sortBy" className="d-flex align-items-center">
            <Form.Label className="me-2 mb-0" style={{ whiteSpace: "nowrap" }}>
              Sort by
            </Form.Label>
            <Form.Select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setSortDirection(sortOrderLabels[e.target.value][1]);
              }}
            >
              {Object.entries(sortOrderLabels).map(
                ([value, [label, _direction]]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                )
              )}
            </Form.Select>
          </Form.Group>
          <Button
            variant="primary"
            onClick={() =>
              setSortDirection(sortDirection === "Asc" ? "Desc" : "Asc")
            }
          >
            {sortDirection === "Asc" ? "↑" : "↓"}
          </Button>
          {isFullSiteList && (
            <Form.Group className="d-flex align-items-center">
              <Form.Select
                value={itemsPerPage}
                onChange={(e) => {
                  const value = e.target.value;
                  setItemsPerPage(value === "all" ? "all" : parseInt(value));
                  setPage(1);
                }}
              >
                <option value="all">One page</option>
                <option value={100}>100 per page</option>
                <option value={500}>500 per page</option>
              </Form.Select>
            </Form.Group>
          )}
          {isFullSiteList && itemsPerPage !== "all" && (
            <>
              <Button
                variant="primary"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
              >
                Prev
              </Button>
              <Button
                variant="primary"
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(totalItems / itemsPerPage)}
              >
                Next
              </Button>
            </>
          )}
        </Col>
      </Row>
      <Table striped bordered responsive>
        <thead>
          <tr>
            <th>
              <Form.Check
                type={"checkbox"}
                checked={selectedSiteIds.size === unlockedSites.length}
                onChange={() => {
                  if (selectedSiteIds.size !== unlockedSites.length) {
                    // Fewer than all selected, toggle to all selected
                    setSelectedSiteIds(
                      new Set(unlockedSites.map((site) => site.id))
                    );
                  } else {
                    // All selected, toggle to none selected
                    setSelectedSiteIds(new Set());
                  }
                }}
                data-testid={"orgsites-selectall"}
              />
            </th>
            <th>Site</th>
            <th>Energy / tariffs</th>
            <th>Status / hardware / SDM</th>
            <th>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "space-between",
                }}
              >
                <div>Notes</div>
                {!isFullSiteList && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setInlineEditMode(!inlineEditMode);
                      if (inlineEditMode) {
                        // Inline edit mode disabled, discard edits
                        setInlineEdits({});
                      }
                    }}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <Form.Check
                      type="switch"
                      label="Inline edit"
                      checked={inlineEditMode}
                      readOnly
                      inline
                    />
                  </div>
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedSites.map((site, ii) => (
            <MemoizedSitesTableRow
              isFullSiteList={isFullSiteList}
              siteValuesOptions={fetchSiteValues.data.options}
              key={site.id}
              index={ii}
              organisation={organisation}
              site={site}
              thisSiteId={thisSiteId}
              testMode={testMode}
              isSelected={selectedSiteIds.has(site.id)}
              setSelected={onSetSelected}
              inlineEditMode={site.locked ? false : inlineEditMode}
              inlineEdits={inlineEdits[site.id]}
              setInlineEdits={onSetInlineEdits}
              setInlineSiteCalculations={setInlineSiteCalculations}
            />
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const SelectedSitesStatistics = ({
  organisation,
  selectedSites,
  selectedSiteIds,
  inlineEdits,
  inlineSiteCalculations,
}: {
  organisation: Organisation;
  selectedSites: Site[];
  selectedSiteIds: Set<string>;
  inlineEdits: {
    [key: string]: { site: RecursivePartialSiteAllowStringValues };
  };
  inlineSiteCalculations: { [key: string]: SiteCalculations };
}) => {
  const latestEditValue = (site: Site) => {
    if (inlineEdits[site.id]) {
      return inlineEdits[site.id].site;
    }
    return site;
  };
  const latestCalcValue = (site: Site) => {
    if (inlineSiteCalculations[site.id]) {
      return inlineSiteCalculations[site.id];
    }
    return site;
  };
  const totalIrrMultipliedByInstalledCapacity = selectedSites.reduce(
    (acc, site) => {
      const ownership = getOwnership(site, organisation);
      const irrOwnership =
        ownership === "landlord" || ownership === "ownerOccupier"
          ? ownership
          : "landlord";
      const irr =
        latestCalcValue(site).financialModels![irrOwnership!]["license"].irr;
      if (
        irr !== undefined &&
        parseAsNumber(latestEditValue(site).totalInstalledCapacity) > 0
      ) {
        return (
          acc +
          irr * parseAsNumber(latestEditValue(site).totalInstalledCapacity)
        );
      }
      return acc;
    },
    0
  );
  const statsTotalInstalledCapacity = selectedSites.reduce(
    (acc, site) =>
      acc + parseAsNumber(latestEditValue(site).totalInstalledCapacity),
    0
  );
  const statsAverageIrr =
    statsTotalInstalledCapacity > 0
      ? totalIrrMultipliedByInstalledCapacity / statsTotalInstalledCapacity
      : 0;
  const statsTotalMWp = statsTotalInstalledCapacity / 1000;
  const statsTotalGenerationKwh = selectedSites.reduce(
    (acc, site) => acc + latestCalcValue(site).energyFlowAnnual!.generation,
    0
  );
  const statsTotalBehindMeterKwh = selectedSites.reduce(
    (acc, site) => acc + latestCalcValue(site).energyFlowAnnual!.behindMeter,
    0
  );
  const statsTotalTenantShare =
    statsTotalGenerationKwh > 0
      ? statsTotalBehindMeterKwh / statsTotalGenerationKwh
      : 0;

  return (
    <div>
      Org stats{selectedSiteIds.size > 0 && ` (selected)`}: Portfolio IRR:{" "}
      <strong>{formatPercentage(statsAverageIrr, 2)}</strong>, Total MW:{" "}
      <strong>{formatUnits(statsTotalMWp, "MW", 2)}</strong>, Total generation
      (kWh): <strong>{formatUnits(statsTotalGenerationKwh, "kWh", 2)}</strong>,
      Total tenant share:{" "}
      <strong>{formatPercentage(statsTotalTenantShare, 2)}</strong>
    </div>
  );
};

export default SitesTable;
